import { useEffect } from 'react';
import { homeFaq, rentalsFaq, transfersFaq, toursFaq } from '../data/faq';
import { VITO, vitoFaq } from '../data/fleet';

const BASE_URL = 'https://montreon.ro';

const ADDRESS = {
  "@type": "PostalAddress",
  "streetAddress": "Str. Avram Iancu nr. 56, ap. 2",
  "addressLocality": "Brașov",
  "postalCode": "500075",
  "addressCountry": "RO",
};

const ORGANIZATION = {
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "Montréon",
  "legalName": "Quantive Endeavors SRL",
  "taxID": "RO54042200",
  "url": BASE_URL,
  "logo": `${BASE_URL}/og-image.png`,
  "telephone": "+40726375166",
  "email": "contact@montreon.ro",
  "address": ADDRESS,
  "sameAs": ["https://facebook.com/montreon.ro", "https://instagram.com/montreon.ro"],
};

const PROVIDER = { "@id": `${BASE_URL}/#organization` };

const LOCAL_BUSINESS = {
  "@type": "LocalBusiness",
  "@id": `${BASE_URL}/#localbusiness`,
  "name": "Montréon",
  "legalName": "Quantive Endeavors SRL",
  "url": BASE_URL,
  "image": `${BASE_URL}/og-image.png`,
  "telephone": "+40726375166",
  "email": "contact@montreon.ro",
  "address": ADDRESS,
  "areaServed": ["Brașov", "București", "Sibiu", "Cluj-Napoca", "Târgu Mureș"],
  "priceRange": "€€",
  "currenciesAccepted": "EUR, RON",
  "paymentAccepted": "Cash, Credit Card, Bank Transfer",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59",
  },
  "parentOrganization": PROVIDER,
};

const faqSchema = (items, isRo) => ({
  "@type": "FAQPage",
  "mainEntity": items.map((f) => ({
    "@type": "Question",
    "name": isRo ? f.qRo : f.qEn,
    "acceptedAnswer": { "@type": "Answer", "text": isRo ? f.aRo : f.aEn },
  })),
});

const seoData = {
  '/': {
    titleRo: 'Montréon Brașov | Închirieri Auto, Transfer Aeroport & Tururi Turistice',
    titleEn: 'Montréon Brașov | Car Rentals, Airport Transfers & Touristic Tours',
    descRo: 'Montréon: închirieri auto, transferuri private aeroport și tururi turistice premium în Brașov și Transilvania. Șoferi profesioniști, vehicule moderne, prețuri transparente.',
    descEn: 'Montréon: car rentals, private airport transfers and premium touristic tours in Brașov and Transylvania. Professional drivers, modern vehicles, transparent prices.',
    canonical: '/',
    schema: (isRo) => [ORGANIZATION, LOCAL_BUSINESS, faqSchema(homeFaq, isRo)],
  },
  '/rent-a-car': {
    titleRo: 'Închirieri Auto Brașov | Montréon Rent a Car — Prețuri de la 35€/zi',
    titleEn: 'Car Rental Brașov | Montréon Rent a Car — From €35/day',
    descRo: 'Închiriază o mașină în Brașov: Mercedes E200, Volkswagen Passat, Skoda Superb, Mercedes E300de, Tesla Model 3, Mercedes Vito. Kilometraj nelimitat, livrare gratuită la aeroport.',
    descEn: 'Rent a car in Brașov: Mercedes E200, Volkswagen Passat, Skoda Superb, Mercedes E300de, Tesla Model 3, Mercedes Vito. Unlimited mileage, free airport delivery.',
    canonical: '/rent-a-car',
    schema: (isRo) => [
      {
        "@type": "AutoRental",
        "name": "Montréon Rent a Car Brașov",
        "url": `${BASE_URL}/rent-a-car`,
        "description": isRo
          ? "Închirieri auto Brașov cu flotă modernă: Mercedes E200, Volkswagen Passat, Skoda Superb, Mercedes E300de, Tesla Model 3, Mercedes Vito Tourer. Kilometraj nelimitat, livrare gratuită în Brașov și la aeroport."
          : "Car rental in Brașov with a modern fleet: Mercedes E200, Volkswagen Passat, Skoda Superb, Mercedes E300de, Tesla Model 3, Mercedes Vito Tourer. Unlimited mileage, free delivery in Brașov and at the airport.",
        "telephone": "+40726375166",
        "email": "contact@montreon.ro",
        "address": ADDRESS,
        "areaServed": "Brașov",
        "priceRange": "€35-€90",
        "parentOrganization": PROVIDER,
      },
      faqSchema(rentalsFaq, isRo),
    ],
  },
  '/corporate': {
    noindex: true,
    titleRo: 'Transport Corporate Brașov | Montréon — Transport Angajați pentru Companii',
    titleEn: 'Corporate Transport Brașov | Montréon — Staff Transport for Companies',
    descRo: 'Transport zilnic pentru angajații și echipele companiilor din Brașov: navete, transport în ture, deplasări de business. Contract, facturare lunară, șoferi profesioniști. În curând.',
    descEn: 'Daily transport for company employees and teams in Brașov: commutes, shift transport, business trips. Contract, monthly invoicing, professional drivers. Coming soon.',
    canonical: '/corporate',
    schema: () => [
      {
        "@type": "Service",
        "serviceType": "Corporate staff transport",
        "name": "Montréon Transport Corporate Brașov",
        "url": `${BASE_URL}/corporate`,
        "description": "Transport zilnic pentru angajații companiilor din Brașov: navete, ture, deplasări de business.",
        "areaServed": "Brașov",
        "provider": PROVIDER,
      },
    ],
  },
  [VITO.slugRo]: {
    titleRo: 'Închiriere Minivan Brașov – Mercedes Vito 7+1 Locuri de la 80 €/zi | Montréon',
    titleEn: 'Minivan Rental Brașov – Mercedes Vito 7+1 Seats from 80 €/day | Montréon',
    descRo: 'Închiriază Mercedes-Benz Vito Tourer 7+1 locuri în Brașov, 80 €/zi: automat, diesel, 7 valize, km nelimitați, livrare gratuită, anvelope de iarnă incluse.',
    descEn: 'Rent a Mercedes-Benz Vito Tourer 7+1 seater in Brașov for €80/day: automatic, diesel, 7 suitcases, unlimited km, free airport delivery, winter tyres included.',
    canonical: VITO.slugRo,
    canonicalEn: VITO.slugEn,
    schema: (isRo) => [
      {
        "@type": "Car",
        "@id": `${BASE_URL}${VITO.slugRo}#car`,
        "name": `${VITO.name} ${VITO.year}`,
        "brand": { "@type": "Brand", "name": "Mercedes-Benz" },
        "model": "Vito Tourer",
        "vehicleModelDate": String(VITO.year),
        "vehicleSeatingCapacity": 8,
        "fuelType": "Diesel",
        "vehicleTransmission": "Automatic",
        "image": VITO.images.map((i) => `${BASE_URL}${i.src}`),
        "url": `${BASE_URL}${isRo ? VITO.slugRo : VITO.slugEn}`,
        "offers": {
          "@type": "Offer",
          "price": String(VITO.pricePerDay),
          "priceCurrency": "EUR",
          "priceSpecification": { "@type": "UnitPriceSpecification", "price": String(VITO.pricePerDay), "priceCurrency": "EUR", "unitText": "DAY" },
          "availability": "https://schema.org/InStock",
          "areaServed": "Brașov",
          "url": `${BASE_URL}${isRo ? VITO.slugRo : VITO.slugEn}`,
          "offeredBy": PROVIDER,
        },
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": isRo ? "Acasă" : "Home", "item": isRo ? `${BASE_URL}/` : `${BASE_URL}/en` },
          { "@type": "ListItem", "position": 2, "name": isRo ? "Închirieri Auto" : "Car Rentals", "item": `${BASE_URL}${isRo ? '' : '/en'}/rent-a-car` },
          { "@type": "ListItem", "position": 3, "name": isRo ? "Închiriere Minivan Mercedes Vito în Brașov – 7+1 locuri" : "Mercedes Vito Minivan Rental in Brașov – 7+1 Seats", "item": `${BASE_URL}${isRo ? VITO.slugRo : VITO.slugEn}` },
        ],
      },
      faqSchema(vitoFaq, isRo),
    ],
  },
  '/transfers': {
    titleRo: 'Transfer Aeroport Brașov | Montréon — Transferuri Private România',
    titleEn: 'Brașov Airport Transfer | Montréon — Private Transfers Romania',
    descRo: 'Transfer aeroport Brașov-Ghimbav de la 150 RON, Otopeni de la 650 RON, Sibiu, Cluj, Târgu Mureș. Prețuri fixe, monitorizare zbor, vehicule premium Mercedes & Skoda. Rezervă online.',
    descEn: 'Airport transfer Brașov-Ghimbav from 150 RON, Otopeni from 650 RON, Sibiu, Cluj, Târgu Mureș. Fixed prices, flight monitoring, premium Mercedes & Skoda vehicles. Book online.',
    canonical: '/transfers',
    schema: (isRo) => [
      {
        "@type": "TaxiService",
        "name": "Montréon Transfer Aeroport Brașov",
        "url": `${BASE_URL}/transfers`,
        "description": isRo
          ? "Transferuri private cu șofer din Brașov către aeroporturile Brașov-Ghimbav, București Otopeni, Băneasa, Sibiu, Târgu-Mureș și Cluj-Napoca. Prețuri fixe pe cursă, monitorizarea zborului, prima oră de așteptare gratuită, 24/7."
          : "Private chauffeured transfers from Brașov to Brașov-Ghimbav, Bucharest Otopeni, Băneasa, Sibiu, Târgu-Mureș and Cluj-Napoca airports. Fixed price per trip, flight monitoring, first hour of waiting free, 24/7.",
        "telephone": "+40726375166",
        "provider": PROVIDER,
        "areaServed": ["Brașov", "București", "Cluj-Napoca", "Sibiu", "Târgu Mureș"],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": isRo ? "Prețuri transferuri din Brașov (o direcție)" : "Transfer prices from Brașov (one way)",
          "itemListElement": [
            { "@type": "Offer", "name": "Brașov → Aeroportul Brașov-Ghimbav (GHV)", "price": "150", "priceCurrency": "RON", "description": "Standard 150 RON · Premium 200 RON · Minivan 7+1 200 RON" },
            { "@type": "Offer", "name": "Brașov → Aeroportul București Otopeni (OTP)", "price": "650", "priceCurrency": "RON", "description": "Standard 650 RON · Premium 800 RON · Minivan 7+1 850 RON" },
            { "@type": "Offer", "name": "Brașov → Aeroportul București Băneasa (BBU)", "price": "700", "priceCurrency": "RON", "description": "Standard 700 RON · Premium 850 RON · Minivan 7+1 900 RON" },
            { "@type": "Offer", "name": "Brașov → Aeroportul Sibiu (SBZ)", "price": "650", "priceCurrency": "RON", "description": "Standard 650 RON · Premium 800 RON · Minivan 7+1 850 RON" },
            { "@type": "Offer", "name": "Brașov → Aeroportul Târgu-Mureș (TGM)", "price": "750", "priceCurrency": "RON", "description": "Standard 750 RON · Premium 900 RON · Minivan 7+1 950 RON" },
            { "@type": "Offer", "name": "Brașov → Aeroportul Cluj-Napoca (CLJ)", "price": "1200", "priceCurrency": "RON", "description": "Standard 1200 RON · Premium 1500 RON · Minivan 7+1 1600 RON" },
          ],
        },
      },
      faqSchema(transfersFaq, isRo),
    ],
  },
  '/tours': {
    titleRo: 'Tururi Private Transilvania | Montréon — Castelul Bran, Peleș, Transfăgărășan',
    titleEn: 'Private Tours Transylvania | Montréon — Bran Castle, Peleș, Transfăgărășan',
    descRo: 'Tururi private din Brașov: Castelul Bran, Castelul Peleș, Transfăgărășan, Sighișoara, Viscri, Transbucegi, Salina Turda. Transport premium, program flexibil. De la 500 RON.',
    descEn: 'Private tours from Brașov: Bran Castle, Peleș Castle, Transfăgărășan, Sighișoara, Viscri, Transbucegi, Turda Salt Mine. Premium transport, flexible schedule. From 500 RON.',
    canonical: '/tours',
    schema: (isRo) => [
      {
        "@type": "TouristTrip",
        "name": isRo ? "Tururi Private Transilvania — Montréon" : "Private Transylvania Tours — Montréon",
        "url": `${BASE_URL}/tours`,
        "description": isRo
          ? "Tururi private de o zi din Brașov cu șofer și transport premium: Castelul Bran și Râșnov, Peleș și Cantacuzino, Transfăgărășan, Transbucegi, Sighișoara și Viscri, Salina Turda și Sibiu. Preluare de la hotel, program flexibil, prețuri per mașină."
          : "Private day tours from Brașov with driver and premium transport: Bran Castle and Râșnov, Peleș and Cantacuzino, Transfăgărășan, Transbucegi, Sighișoara and Viscri, Turda Salt Mine and Sibiu. Hotel pickup, flexible schedule, prices per car.",
        "touristType": "Private Tour",
        "provider": PROVIDER,
        "offers": [
          { "@type": "Offer", "name": "Castelul Bran + Cetatea Râșnov", "price": "500", "priceCurrency": "RON", "description": "Standard 500 RON · Premium 600 RON · Minivan 7+1 700 RON, per mașină, transport din Brașov și retur" },
          { "@type": "Offer", "name": "Castelul Peleș + Castelul Cantacuzino", "price": "500", "priceCurrency": "RON", "description": "Standard 500 RON · Premium 600 RON · Minivan 7+1 700 RON, per mașină, transport din Brașov și retur" },
          { "@type": "Offer", "name": "Transfăgărășan", "price": "650", "priceCurrency": "RON", "description": "Standard 650 RON · Premium 750 RON · Minivan 7+1 850 RON, per mașină, transport din Brașov și retur" },
          { "@type": "Offer", "name": "Sighișoara & Viscri", "price": "700", "priceCurrency": "RON", "description": "Standard 700 RON · Premium 825 RON · Minivan 7+1 950 RON, per mașină, transport din Brașov și retur" },
          { "@type": "Offer", "name": "Transbucegi", "price": "650", "priceCurrency": "RON", "description": "Standard 650 RON · Premium 750 RON · Minivan 7+1 850 RON, per mașină, transport din Brașov și retur" },
          { "@type": "Offer", "name": "Sighișoara + Salina Turda + Sibiu", "price": "1400", "priceCurrency": "RON", "description": "Standard 1400 RON · Premium 1600 RON · Minivan 7+1 1800 RON, per mașină, transport din Brașov și retur" },
        ],
      },
      faqSchema(toursFaq, isRo),
    ],
  },
  '/contact': {
    titleRo: 'Contact Montréon | Închirieri Auto, Transferuri & Tururi Brașov',
    titleEn: 'Contact Montréon | Car Rental, Transfers & Tours Brașov',
    descRo: 'Contactează Montréon pentru închirieri auto, transferuri aeroport și tururi private în Brașov. Telefon: +40 726 375 166. Email: contact@montreon.ro.',
    descEn: 'Contact Montréon for car rentals, airport transfers and private tours in Brașov. Phone: +40 726 375 166. Email: contact@montreon.ro.',
    canonical: '/contact',
    schema: () => [{ "@type": "ContactPage", "url": `${BASE_URL}/contact`, "mainEntity": ORGANIZATION }],
  },
  '/termeni-si-conditii': {
    noindex: true,
    titleRo: 'Termeni și Condiții | Montréon',
    titleEn: 'Terms and Conditions | Montréon',
    descRo: 'Termenii și condițiile generale pentru serviciile Montréon operate de Quantive Endeavors SRL.',
    descEn: 'General terms and conditions for Montréon services operated by Quantive Endeavors SRL.',
    canonical: '/termeni-si-conditii',
  },
  '/politica-de-confidentialitate': {
    noindex: true,
    titleRo: 'Politica de Confidențialitate | Montréon',
    titleEn: 'Privacy Policy | Montréon',
    descRo: 'Politica de confidențialitate Montréon — cum prelucrăm datele personale.',
    descEn: 'Montréon privacy policy — how we process personal data.',
    canonical: '/politica-de-confidentialitate',
  },
  '/politica-de-cookie': {
    noindex: true,
    titleRo: 'Politica de Cookie-uri | Montréon',
    titleEn: 'Cookie Policy | Montréon',
    descRo: 'Politica de cookie-uri a site-ului Montréon.',
    descEn: 'Cookie policy of the Montréon website.',
    canonical: '/politica-de-cookie',
  },
};

const setMeta = (name, content, attr = 'name') => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setHreflang = (lang, href) => {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', lang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export const clearRouteSeo = () => {
  document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang], #seo-jsonld').forEach((el) => el.remove());
};

export const routeUrls = (canonicalPath, canonicalEnPath) => {
  const ro = `${BASE_URL}${canonicalPath}`;
  const en = canonicalEnPath ? `${BASE_URL}${canonicalEnPath}` : canonicalPath === '/' ? `${BASE_URL}/en` : `${BASE_URL}/en${canonicalPath}`;
  return { ro, en };
};

export const SEO = ({ path, language = 'ro' }) => {
  const isRo = language === 'ro';
  const data = seoData[path] || seoData['/'];

  useEffect(() => {
    const title = isRo ? data.titleRo : data.titleEn;
    const description = isRo ? data.descRo : data.descEn;
    const urls = routeUrls(data.canonical, data.canonicalEn);
    const canonical = isRo ? urls.ro : urls.en;

    document.title = title;
    document.documentElement.lang = isRo ? 'ro' : 'en';
    setMeta('title', title);
    setMeta('description', description);
    setMeta('robots', data.noindex ? 'noindex,follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    document.querySelector('meta[name="prerender-status-code"]')?.remove();
    setLink('canonical', canonical);
    if (data.noindex) {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    } else {
      setHreflang('ro', urls.ro);
      setHreflang('en', urls.en);
      setHreflang('x-default', urls.ro);
    }
    setMeta('og:locale', isRo ? 'ro_RO' : 'en_US', 'property');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('twitter:title', title);
    setMeta('twitter:url', canonical);
    setMeta('twitter:description', description);

    const existingLd = document.getElementById('seo-jsonld');
    if (existingLd) existingLd.remove();
    if (data.schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'seo-jsonld';
      script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": data.schema(isRo) });
      document.head.appendChild(script);
    }

    return () => {
      const ld = document.getElementById('seo-jsonld');
      if (ld) ld.remove();
    };
  }, [path, isRo, data]);

  return null;
};
